import type { CSSProperties } from 'react';

// Line icons on a 24×24 grid — the subset of mobile/components/icons/
// icon-data.ts the web uses (same path data, so both clients draw the
// same glyphs), plus trash / sun / moon / car / message and the
// device-category glyphs. Round caps and joins, currentColor strokes.
// Cut-out fills (the settings knobs) read --icon-cut, which each
// container sets to its own background.

const ICON_PATHS: Record<string, string> = {
  add: '<path d="M12 5 V19"/><path d="M5 12 H19"/>',
  check: '<path d="M5 12.5 L10 17.5 L19 7"/>',
  chevronLeft: '<path d="M14.5 6 L8.5 12 L14.5 18"/>',
  chevronRight: '<path d="M9.5 6 L15.5 12 L9.5 18"/>',
  chevronDown: '<path d="M6 9.5 L12 15.5 L18 9.5"/>',
  close: '<path d="M6 6 L18 18"/><path d="M18 6 L6 18"/>',
  calendar: '<path d="M4 6 H20 V20 H4 Z"/><path d="M4 9.5 H20"/><path d="M8 4 V7"/><path d="M16 4 V7"/>',
  settings:
    '<path d="M4 7.5 H20"/><path d="M4 12 H20"/><path d="M4 16.5 H20"/><circle cx="9" cy="7.5" r="2" style="fill:var(--icon-cut, var(--surface))"/><circle cx="15" cy="12" r="2" style="fill:var(--icon-cut, var(--surface))"/><circle cx="8" cy="16.5" r="2" style="fill:var(--icon-cut, var(--surface))"/>',
  home: '<path d="M4 11.5 L12 4 L20 11.5"/><path d="M6 10 V20 H18 V10"/>',
  receipt: '<path d="M6 3.5 H18 V20.5 L15 18.5 L12 20.5 L9 18.5 L6 20.5 Z"/><path d="M9 8.5 H15"/><path d="M9 12.5 H15"/>',
  card: '<path d="M3.5 6 H20.5 V18 H3.5 Z"/><path d="M3.5 10 H20.5"/><path d="M7 14.5 H10.5"/>',
  transfer:
    '<path d="M4 8.5 H18"/><path d="M14.5 5 L18 8.5 L14.5 12"/><path d="M20 15.5 H6"/><path d="M9.5 12 L6 15.5 L9.5 19"/>',
  flight: '<path d="M3 11 L21 4 L13.5 21 L11 13 Z"/><path d="M11 13 L21 4"/>',
  train:
    '<path d="M7 4 H17 V15 A2 2 0 0 1 15 17 H9 A2 2 0 0 1 7 15 Z"/><path d="M7 10 H17"/><path d="M9 20 L7 22"/><path d="M15 20 L17 22"/><circle cx="9.5" cy="13.5" r="0.7" fill="currentColor" stroke="none"/><circle cx="14.5" cy="13.5" r="0.7" fill="currentColor" stroke="none"/>',
  drive:
    '<path d="M4 13 L6 8 H18 L20 13"/><path d="M3.5 13 H20.5 V16.5 H3.5 Z"/><circle cx="7.5" cy="16.5" r="1.4"/><circle cx="16.5" cy="16.5" r="1.4"/>',
  hotel: '<path d="M4 7 V17"/><path d="M4 12 H20 V17"/><path d="M4 12 V9.5 H10 V12"/>',
  dining: '<circle cx="12" cy="12" r="7.5"/><circle cx="12" cy="12" r="3"/>',
  star: '<path d="M12 3.5 L14.12 9.09 L20.08 9.37 L15.42 13.11 L17 18.88 L12 15.6 L7 18.88 L8.58 13.11 L3.92 9.37 L9.88 9.09 Z"/>',
  refresh: '<path d="M19 7 A8 8 0 1 0 20 12"/><path d="M19 3.5 V7.2 H15.3"/>',
  external: '<path d="M13 5 H19 V11"/><path d="M19 5 L11 13"/><path d="M18 13.5 V19 H5 V6 H10.5"/>',
  info: '<circle cx="12" cy="12" r="8"/><path d="M12 11 V16"/><circle cx="12" cy="8" r="0.8" fill="currentColor" stroke="none"/>',
  trash: '<path d="M4.5 7 H19.5"/><path d="M9.5 7 V4.5 H14.5 V7"/><path d="M6.5 7 L7.5 19.5 H16.5 L17.5 7"/>',
  sun: '<circle cx="12" cy="12" r="3.8"/><path d="M12 3 V5"/><path d="M12 19 V21"/><path d="M3 12 H5"/><path d="M19 12 H21"/><path d="M5.6 5.6 L7 7"/><path d="M17 17 L18.4 18.4"/><path d="M18.4 5.6 L17 7"/><path d="M7 17 L5.6 18.4"/>',
  moon: '<path d="M19.5 14.2 A7.8 7.8 0 1 1 9.8 4.5 A6.2 6.2 0 0 0 19.5 14.2 Z"/>',
  account:
    '<circle cx="12" cy="8.2" r="3.4"/><path d="M5.5 19.5 C5.5 15.8 8.4 14 12 14 C15.6 14 18.5 15.8 18.5 19.5"/>',
  // The Dogs and Car sections, the manuals list; paw and file are also
  // iOS Today's icons for the dogs' plans and notes from email.
  paw:
    '<circle cx="6.2" cy="10.4" r="1.9"/><circle cx="9.7" cy="6.2" r="1.9"/><circle cx="14.3" cy="6.2" r="1.9"/><circle cx="17.8" cy="10.4" r="1.9"/><path d="M12 12.6 C9.3 12.6 7 15.3 7 17.5 C7 19.1 8.2 20 9.7 19.8 C10.7 19.7 11.2 19.3 12 19.3 C12.8 19.3 13.3 19.7 14.3 19.8 C15.8 20 17 19.1 17 17.5 C17 15.3 14.7 12.6 12 12.6 Z"/>',
  car:
    '<path d="M5 12 L6.8 7.2 A1.5 1.5 0 0 1 8.2 6.2 H15.8 A1.5 1.5 0 0 1 17.2 7.2 L19 12"/><path d="M4 12 H20 V17 H4 Z"/><path d="M6 17 V19"/><path d="M18 17 V19"/><circle cx="7.5" cy="14.5" r="0.8" fill="currentColor" stroke="none"/><circle cx="16.5" cy="14.5" r="0.8" fill="currentColor" stroke="none"/>',
  file: '<path d="M6.5 3.5 H14 L18.5 8 V20.5 H6.5 Z"/><path d="M14 3.5 V8 H18.5"/><path d="M9.5 12.5 H15.5"/><path d="M9.5 16 H15.5"/>',
  // A reminder row on Today ("Text Dusan at PDX Paws"); same path on iOS.
  bell: '<path d="M5.5 17 H18.5 L17 14.5 V10.5 A5 5 0 0 0 7 10.5 V14.5 Z"/><path d="M10 17 A2 2 0 0 0 14 17"/><path d="M12 4 V5.5"/>',
  // Web-only: the Inbox section (email capture).
  message: '<path d="M4.5 5.5 H19.5 V16 H11 L7 19.5 V16 H4.5 Z"/><path d="M8.5 9.5 H15.5"/><path d="M8.5 12.5 H13"/>',
  checklist: '<path d="M10.5 7 H19.5"/><path d="M10.5 12 H19.5"/><path d="M10.5 17 H19.5"/><path d="M4.5 7 L5.8 8.3 L8 5.8"/><path d="M4.5 12 L5.8 13.3 L8 10.8"/><path d="M4.5 17 L5.8 18.3 L8 15.8"/>',
  inbox: '<path d="M4 13.5 L6.5 5.5 H17.5 L20 13.5 V19 H4 Z"/><path d="M4 13.5 H9 L10.2 15.5 H13.8 L15 13.5 H20"/>',
  // Web-only: the device categories on Home (lib/house.ts CATEGORY_ICON).
  thermometer: '<path d="M10 13.6 V5.5 A2 2 0 0 1 14 5.5 V13.6 A4 4 0 1 1 10 13.6 Z"/><path d="M12 9.5 V16.5"/>',
  wind: '<path d="M3.5 9 H14 A2.5 2.5 0 1 0 11.5 6.5"/><path d="M3.5 13 H17.5 A2.5 2.5 0 1 1 15 15.5"/><path d="M3.5 17 H9"/>',
  sensor:
    '<circle cx="12" cy="12" r="2"/><path d="M8 8 A5.66 5.66 0 0 0 8 16"/><path d="M16 8 A5.66 5.66 0 0 1 16 16"/><path d="M5.2 5.2 A9.6 9.6 0 0 0 5.2 18.8"/><path d="M18.8 5.2 A9.6 9.6 0 0 1 18.8 18.8"/>',
  shield: '<path d="M12 3.5 L19 6.5 V11.5 C19 15.8 16 18.9 12 20.5 C8 18.9 5 15.8 5 11.5 V6.5 Z"/>',
  camera: '<path d="M3.5 8 H7 L8.5 5.5 H15.5 L17 8 H20.5 V18.5 H3.5 Z"/><circle cx="12" cy="13" r="3.2"/>',
  lock: '<path d="M5.5 11 H18.5 V20 H5.5 Z"/><path d="M8.5 11 V8 A3.5 3.5 0 0 1 15.5 8 V11"/>',
  vacuum: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="8.5" r="1.6"/><path d="M7.5 15.5 H16.5"/>',
  washer: '<path d="M5 3.5 H19 V20.5 H5 Z"/><circle cx="12" cy="13" r="4.2"/><path d="M8 6.5 H9.5"/><path d="M13 6.5 H16"/>',
  droplet: '<path d="M12 3.5 C12 3.5 6 10.2 6 14.2 A6 6 0 0 0 18 14.2 C18 10.2 12 3.5 12 3.5 Z"/>',
  bulb: '<path d="M9 17 V15.2 C7.2 14 6 12.1 6 10 A6 6 0 0 1 18 10 C18 12.1 16.8 14 15 15.2 V17 Z"/><path d="M9.5 20.5 H14.5"/>',
  wifi: '<path d="M3.5 9.5 A12 12 0 0 1 20.5 9.5"/><path d="M6.5 13 A8 8 0 0 1 17.5 13"/><path d="M9.5 16.3 A3.6 3.6 0 0 1 14.5 16.3"/><circle cx="12" cy="19.2" r="0.9" fill="currentColor" stroke="none"/>',
  printer: '<path d="M7 8.5 V3.5 H17 V8.5"/><path d="M7 17 H4.5 V8.5 H19.5 V17 H17"/><path d="M7 13.5 H17 V20.5 H7 Z"/>',
  chip: '<path d="M7 7 H17 V17 H7 Z"/><path d="M10 7 V4"/><path d="M14 7 V4"/><path d="M10 20 V17"/><path d="M14 20 V17"/><path d="M4 10 H7"/><path d="M4 14 H7"/><path d="M17 10 H20"/><path d="M17 14 H20"/>',
  plug: '<path d="M9 3.5 V7.5"/><path d="M15 3.5 V7.5"/><path d="M6.5 7.5 H17.5 V11 A5.5 5.5 0 0 1 6.5 11 Z"/><path d="M12 16.5 V20.5"/>',
};

export type IconName = keyof typeof ICON_PATHS;

export default function Icon({
  name,
  size = 20,
  stroke = 1.8,
  style,
  className,
}: {
  name: string;
  size?: number;
  stroke?: number;
  style?: CSSProperties;
  className?: string;
}) {
  const p = ICON_PATHS[name] ?? ICON_PATHS.info;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={{ flexShrink: 0, ...style }}
      dangerouslySetInnerHTML={{ __html: p }}
    />
  );
}
